import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CompanyDto } from 'src/app/data-transfer/entities/doc-entities/company-dto';
import { DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { CoreDataUpdateService } from 'src/app/data-transfer/services/core-data-update-api-service';
import { PathResolveService } from 'src/app/services/path-resolve.service';
import { AuthService } from 'src/app/services/auth-service';

export interface CreateDocDialogData {
  manufacturers: CompanyDto[];
  representatives: CompanyDto[];
  docProfiles: DocProfileDto[];
}

type CompanySelection = CompanyDto | string | null;

@Component({
  selector: 'app-create-doc-dialog',
  templateUrl: './create-doc-dialog.component.html',
  styleUrl: './create-doc-dialog.component.scss',
})
export class CreateDocDialogComponent {
  readonly noDocProfileId = '';
  readonly manufacturers: CompanyDto[];
  readonly representatives: CompanyDto[];
  readonly docProfiles: DocProfileDto[];
  readonly canManageProfiles: boolean;

  form: FormGroup<{
    manufacturer: FormControl<CompanySelection>;
    representative: FormControl<CompanySelection>;
    hasRepresentative: FormControl<boolean>;
    docProfileId: FormControl<string>;
  }>;

  constructor(
    private router: Router,
    private dialogRef: MatDialogRef<CreateDocDialogComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) data: CreateDocDialogData,
    private coreDataService: CoreDataUpdateService,
    authService: AuthService
  ) {
    this.canManageProfiles = authService.canManageOrganization();
    this.manufacturers = data.manufacturers;
    this.representatives = data.representatives;
    this.docProfiles = data.docProfiles;
    const initialManufacturer = data.manufacturers.length === 1 ? data.manufacturers[0] : null;
    this.form = this.fb.group({
      manufacturer: new FormControl<CompanySelection>(initialManufacturer, this.companySelectedValidator()),
      representative: new FormControl<CompanySelection>(
        data.representatives.length === 1 ? data.representatives[0] : null,
        this.optionalCompanySelectedValidator()
      ),
      hasRepresentative: new FormControl<boolean>(false, { nonNullable: true }),
      docProfileId: new FormControl<string>(this.defaultDocProfileId(initialManufacturer), { nonNullable: true }),
    });

    this.form.controls.hasRepresentative.valueChanges.subscribe(() => this.form.controls.representative.updateValueAndValidity());
    this.form.controls.manufacturer.valueChanges.subscribe(value => {
      this.form.controls.docProfileId.setValue(this.defaultDocProfileId(value));
    });
  }

  private defaultDocProfileId(value: CompanySelection): string {
    return (value && typeof value === 'object' ? value.defaultDocProfileId : null) ?? this.noDocProfileId;
  }

  private companySelectedValidator(): ValidatorFn {
    return (control): ValidationErrors | null => (control.value && typeof control.value === 'object' ? null : { required: true });
  }

  private optionalCompanySelectedValidator(): ValidatorFn {
    return (control): ValidationErrors | null => {
      if (!this.form?.controls.hasRepresentative.value) return null;
      return control.value && typeof control.value === 'object' ? null : { required: true };
    };
  }

  get filteredManufacturers(): CompanyDto[] {
    return this.filterCompanies(this.manufacturers, this.form.controls.manufacturer.value);
  }

  get filteredRepresentatives(): CompanyDto[] {
    return this.filterCompanies(this.representatives, this.form.controls.representative.value);
  }

  private filterCompanies(list: CompanyDto[], value: CompanySelection): CompanyDto[] {
    if (typeof value !== 'string') return list;
    const term = value.trim().toLowerCase();
    return term ? list.filter(c => c.companyName?.toLowerCase().includes(term)) : list;
  }

  displayCompany = (value: CompanySelection): string => {
    return typeof value === 'string' ? value : '';
  };

  getCountryName(futureId: string | null): string {
    if (!futureId) return '';
    return this.coreDataService.allCountriesMap.get(futureId)?.name ?? '';
  }

  clickOrganisationSettings(): void {
    this.dialog.closeAll();
    const path = PathResolveService.getOrganizationAdminOrganizationPath(true);
    this.router.navigate([path]);
  }

  clickPpwrProfiles(): void {
    this.dialog.closeAll();
    const path = PathResolveService.getProfilesPath(true);
    this.router.navigate([path]);
  }

  get selectedManufacturer(): CompanyDto | undefined {
    const value = this.form.controls.manufacturer.value;
    return value && typeof value === 'object' ? value : undefined;
  }

  get selectedRepresentative(): CompanyDto | undefined {
    const value = this.form.controls.representative.value;
    return value && typeof value === 'object' ? value : undefined;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onContinue(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const docProfileId = this.form.controls.docProfileId.value;
    this.dialogRef.close({
      confirmed: true,
      manufacturerId: this.selectedManufacturer?.companyProfileId,
      authorizedRepresentativeId: this.form.value.hasRepresentative ? (this.selectedRepresentative?.companyProfileId ?? null) : null,
      docProfileId: docProfileId === this.noDocProfileId ? null : docProfileId,
    });
  }
}
